import { isCopperLayer, matchLayerIdentifier, PcbNodeTypes, PrimitiveRole } from "@buildwithflux/models";
import { SubscriptionManager } from "@buildwithflux/repositories";
import { isCypressTestUserAgent } from "@buildwithflux/shared";
import { UndirectedGraph } from "graphology";
import { connectedComponents } from "graphology-components";
import { Matrix4, Vector2 } from "three";
import { mat3 } from "../../common/geometry/mat3";
import { CurveDivisions } from "../../fills/CopperFillHandler";
import { filterTruthy } from "../../helpers/arrays";
import { PcbNodeHelper } from "../../nodes/pcbNodeHelper";
import { approxAABoundingBox } from "../primitive_store/math/approxAABoundingBox";
import { primitiveToClipper } from "../primitive_store/math/primitiveToClipper";
import { getWorldPositionFromContainer } from "../primitive_store/store/utils";
import { calcCopperFills } from "./copperFills";
import { runConnectivityDetection } from "./runConnectivityDetection";
const segments = CurveDivisions;
export class PcbConnectivityGraph {
    primitiveStore;
    /** A graph with all the pcb copper elements and their current physical connection (not the expected ones from schematic) */
    graph;
    graphNodes = [];
    graphNodesMap = {};
    pcbNodeToGraphNodeMap = new Map();
    // we keep those denormalized versions of the data for easier access, for example we pass the graphNodesMap to
    // web workers since it's easier than passing the entire graph
    /** Keeps track of connected components in the connectivity graph, as a list of connected graph uids */
    connectedComponents = [];
    /** The connected components associated to each net, used later to compute airwires */
    netToConnectedComponents = {};
    /** For now we support only global subscriptions, but if needed we can support granular subscriptions in the future */
    subscriptionManager = new SubscriptionManager();
    /** To solve race conditions with the baked document data being more updated than this store */
    isDataUpdated = false;
    /** Mutex to avoid race conditions with the web worker */
    latestRunId = 0;
    proxyRunConnectivityDetection;
    constructor(primitiveStore, connectivityDetection) {
        this.primitiveStore = primitiveStore;
        this.proxyRunConnectivityDetection =
            isCypressTestUserAgent() || !connectivityDetection ? runConnectivityDetection : connectivityDetection;
        // We build a graph with
        // - Pads, vias and trace endpoints as nodes
        // - Traces and found connections as edges
        this.graph = new UndirectedGraph();
    }
    async updateGraph(inputs) {
        this.latestRunId++;
        const currentRunId = this.latestRunId;
        // eslint-disable-next-line no-console
        console.time("PcbConnectivityGraph.updateGraph");
        // TODO: `fillShapesByNetLayer` is only used to update the connectivity graph, and is not incremental.
        // We should try re-use the shapesMap to avoid this calculation.
        // See https://github.com/buildwithflux/flux-app/issues/8850
        // Copper fills
        const fillShapesByNetLayer = calcCopperFills(inputs);
        const containers = this.primitiveStore.getAllContainersMap();
        const primitives = this.primitiveStore.getAllNodePrimitivesMap();
        const allResults = [];
        for (const [containerUid, container] of containers.entries()) {
            //
            // STEP 1
            // Iterate over all primitives and compute the clipper shape for each one of them
            //
            const shapesPerNode = {};
            const allLayoutHoles = [];
            for (const [, quadTreeNode] of primitives) {
                // We are doing one container at a time
                if (quadTreeNode.containerId !== containerUid)
                    continue;
                for (const primitive of quadTreeNode.primitives) {
                    if (primitive.role === PrimitiveRole.copper && primitive.layerId) {
                        // Ensure the record exists
                        shapesPerNode[quadTreeNode.nodeUid] = shapesPerNode[quadTreeNode.nodeUid] ?? [];
                        const shapeClipper = primitiveToClipper(primitive, segments);
                        // Skip empty shapes being uploaded to the worker, which won't contribute to the connectivity result
                        if (shapeClipper.totalArea() === 0) {
                            continue;
                        }
                        shapesPerNode[quadTreeNode.nodeUid]?.push({
                            shapeObject: shapeClipper.toObject(),
                            layerId: primitive.layerId,
                            ...approxAABoundingBox(primitive),
                        });
                    }
                    else if (primitive.role === PrimitiveRole.layoutHole) {
                        const shapeClipper = primitiveToClipper(primitive, segments);
                        if (shapeClipper.totalArea() === 0) {
                            continue;
                        }
                        allLayoutHoles.push({ shapeObject: shapeClipper.toObject(), ...approxAABoundingBox(primitive) });
                    }
                }
            }
            //
            // STEP 2
            // Add shapes for copper fills as well
            //
            for (const [key, shape] of Object.entries(fillShapesByNetLayer)) {
                const separatedShapes = shape.separateShapes();
                const [fillGeneratorNode, layer] = key.split(":");
                const fillNode = inputs.pcbLayoutNodes[`${fillGeneratorNode}.fill`];
                // TODO: check belonging container/layout
                if (!fillNode || !fillNode.bakedRules || !layer) {
                    continue;
                }
                const transformRelInv = mat3
                    .toThreeMatrix3(fillNode.bakedRules.transformRelativeToShapesLayer)
                    .invert();
                const transformAbs = new Matrix4().fromArray(fillNode.bakedRules.transformRelativeToDocument);
                const layout = fillNode.bakedRules.rootFootprintOrLayoutUid &&
                    inputs.pcbLayoutNodes[fillNode.bakedRules.rootFootprintOrLayoutUid];
                if (!layout || layout.type !== PcbNodeTypes.layout) {
                    continue;
                }
                const foundLayer = Object.values(layout.bakedRules.stackup ?? {}).find((l) => matchLayerIdentifier(l, layer) && isCopperLayer(l));
                if (!foundLayer) {
                    continue;
                }
                for (const shape of separatedShapes) {
                    const shapeAbsolute = shape.clone().transform3Shape(transformRelInv);
                    shapeAbsolute.transform4Shape(transformAbs);
                    const bounds = shapeAbsolute.normalizedBoundsRBush();
                    // Ensure the record exists
                    shapesPerNode[fillNode.uid] = shapesPerNode[fillNode.uid] ?? [];
                    shapesPerNode[fillNode.uid]?.push({
                        shapeObject: shape.toObject(),
                        layerId: foundLayer.uid,
                        ...bounds,
                    });
                }
            }
            const nodeInfos = {};
            for (const [, quadTreeNode] of primitives) {
                // We are doing one container at a time
                if (quadTreeNode.containerId !== containerUid)
                    continue;
                const node = inputs.pcbLayoutNodes[quadTreeNode.nodeUid];
                if (!node || !PcbNodeHelper.isCopperNodeType(node))
                    continue;
                const connectedLayers = ("connectedLayers" in node.bakedRules ? node.bakedRules.connectedLayers : []) ?? [];
                if (node.type === PcbNodeTypes.routeSegment) {
                    connectedLayers?.push(node.bakedRules.layer);
                }
                const netUid = node.bakedRules.effectiveNetId;
                // On sublayout we can have multiple touchpoints at the same location with different nets
                // We want to route only from nets we own, aka nets without the __ separator
                const isNodeUnderSubLayout = Boolean(netUid && PcbNodeHelper.isNodeUnderSubLayout(netUid));
                nodeInfos[quadTreeNode.nodeUid] = {
                    nodeType: node.type,
                    netUid,
                    noRouting: isNodeUnderSubLayout || !netUid,
                    connectedLayers,
                };
            }
            const result = await this.proxyRunConnectivityDetection({
                primitives: Array.from(primitives.values()).map((q) => ({
                    containerId: q.containerId,
                    nodeUid: q.nodeUid,
                })),
                shapesPerNode,
                containerUid,
                allLayoutHoles,
                nodeInfos,
            });
            for (const graphNode of result.graphNodes) {
                // We don't have the containers available in the worker
                graphNode.centerPos = getWorldPositionFromContainer(new Vector2(graphNode.centerPos.x, graphNode.centerPos.y), container);
            }
            allResults.push(result);
        }
        // =================================================================
        // DATA UPDATE
        // We do this as last thing to prevent race conditions
        // NO MORE ASYNC AFTER THIS POINT
        if (currentRunId !== this.latestRunId) {
            // If the run id has changed, it means that a new updateGraph call has been made
            // while we were processing the previous one, so we ignore this one
            return this;
        }
        this.clear();
        for (const result of allResults) {
            for (const graphNode of result.graphNodes) {
                this.graphNodes.push(graphNode);
                this.graphNodesMap[graphNode.uid] = graphNode;
                this.graph.addNode(graphNode.uid, graphNode);
                this.pcbNodeToGraphNodeMap.set(graphNode.nodeUid, [
                    ...(this.pcbNodeToGraphNodeMap.get(graphNode.nodeUid) ?? []),
                    graphNode.uid,
                ]);
            }
            for (const edge of result.foundEdges) {
                const [startUid, endUid] = edge.split("$");
                if (!startUid || !endUid)
                    continue;
                this.graph.addEdge(startUid, endUid, {
                    startUid,
                    endUid,
                });
            }
        }
        // =================================================================
        // CONNECTED COMPONENTS
        this.connectedComponents = connectedComponents(this.graph);
        const netMapArray = Object.entries(inputs.netMap);
        // The connected components associated to each net, used later to compute airwires
        this.netToConnectedComponents = Object.fromEntries(netMapArray.map(([netId, terminalIdsObj]) => {
            // HACK: it's possible that for some reason the net map contains uids that are not pcbLayoutNodes
            // For example, sometimes for the GND net the uid of the gnd schematic object is present
            const terminalIds = Object.keys(terminalIdsObj).filter((id) => inputs.pcbLayoutNodes[id]);
            const connectedComponentsWithNodeUids = this.connectedComponents.map((cc) => {
                return cc.map((c) => this.graph.getNodeAttributes(c).nodeUid);
            });
            // For every net, every terminal should be part of the same connected component
            const connectedComponentsRelatedToNet = Array.from(new Set(filterTruthy(terminalIds.map((terminalId) => connectedComponentsWithNodeUids.find((cc) => cc.includes(terminalId))))));
            return [netId, connectedComponentsRelatedToNet];
        }));
        // eslint-disable-next-line no-console
        console.timeEnd("PcbConnectivityGraph.updateGraph");
        // =================================================================
        // SUBSCRIPTIONS
        this.isDataUpdated = true;
        this.subscriptionManager.notify("update", this);
        return this;
    }
    /**
     * It is recommended to call this method to retrieve a graph node because it first checks
     * existense for you. Otherwise, calling `getNodeAttributes` directly could throw error if
     * node doesn't exist
     */
    getNodeAttributes(nodeId) {
        if (!this.isDataUpdated)
            return null;
        if (!this.graph.hasNode(nodeId))
            return null;
        return this.graph.getNodeAttributes(nodeId);
    }
    getAllNodeAttributes() {
        if (!this.isDataUpdated)
            return {};
        return this.graphNodesMap;
    }
    getConnectedComponents() {
        if (!this.isDataUpdated)
            return undefined;
        return this.connectedComponents;
    }
    getNetToConnectedComponents() {
        if (!this.isDataUpdated)
            return undefined;
        return this.netToConnectedComponents;
    }
    getGraphUidsForPcbNode(nodeId) {
        if (!this.isDataUpdated)
            return [];
        return this.pcbNodeToGraphNodeMap.get(nodeId) ?? [];
    }
    subscribe(callback) {
        return this.subscriptionManager.addSubscription("update", callback);
    }
    clear() {
        this.graph.clear();
        this.graphNodes = [];
        this.graphNodesMap = {};
        this.connectedComponents = [];
        this.netToConnectedComponents = {};
        this.pcbNodeToGraphNodeMap.clear();
        this.isDataUpdated = false;
    }
    invalidateGraph() {
        this.isDataUpdated = false;
    }
}
//# sourceMappingURL=PcbConnectivityGraph.js.map